import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Send, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const checklist = [
  'Store conversion',
  'Paid acquisition',
  'Creative testing',
  'Tracking & attribution',
  'Subscription / IAP revenue',
  'Retention',
];

const LeadForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    workEmail: '',
    company: '',
    appLink: '',
    growthChallenge: '',
    budget: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/app-growth-lead`, form);
      if (res.data.success) {
        toast({
          title: 'Thanks — request received!',
          description: res.data.message,
        });
        setForm({
          name: '',
          workEmail: '',
          company: '',
          appLink: '',
          growthChallenge: '',
          budget: '',
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Something went wrong',
        description: 'Please try again or email arjun@upscale.no directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="app-growth-lead-form"
      data-testid="app-growth-lead-form"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-20 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="max-w-3xl mb-12 text-center mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
            Want to know where your app growth is{' '}
            <span className="text-blue-600">leaking?</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            If your app is already live and you want more users, better conversion, or stronger
            revenue growth, we can help you identify the next best move.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 items-start">
          {/* Left: Checklist */}
          <div className="lg:pt-2">
            <div className="text-sm uppercase tracking-widest text-blue-600 font-semibold mb-6">
              We'll look at
            </div>
            <ul className="space-y-4">
              {checklist.map((it, i) => (
                <li key={i} className="flex items-center gap-3 text-lg text-gray-900">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  {it}
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-white border border-blue-100 rounded-2xl shadow-sm">
              <p className="text-sm text-gray-700 leading-relaxed">
                Secondary offer — <span className="text-gray-900 font-semibold">Send us your app</span>{' '}
                in the form and we'll share <span className="text-blue-600 font-semibold">3 growth observations</span>{' '}
                back within 3 working days, no strings attached.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-blue-100">
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <Input
                    data-testid="lead-name"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    placeholder="Full name"
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work email *
                  </label>
                  <Input
                    data-testid="lead-email"
                    name="workEmail"
                    type="email"
                    required
                    value={form.workEmail}
                    onChange={onChange}
                    placeholder="you@company.no"
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company *
                </label>
                <Input
                  data-testid="lead-company"
                  name="company"
                  required
                  value={form.company}
                  onChange={onChange}
                  placeholder="Company name"
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  App name / App Store or Google Play link
                </label>
                <Input
                  data-testid="lead-app"
                  name="appLink"
                  value={form.appLink}
                  onChange={onChange}
                  placeholder="https://apps.apple.com/... or app name"
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main growth challenge *
                </label>
                <Textarea
                  data-testid="lead-challenge"
                  name="growthChallenge"
                  required
                  value={form.growthChallenge}
                  onChange={onChange}
                  placeholder="What is your app trying to grow? Where does it feel stuck?"
                  rows={4}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly paid media budget range{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  data-testid="lead-budget"
                  name="budget"
                  value={form.budget}
                  onChange={onChange}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select budget range</option>
                  <option value="< NOK 20K/mo">Below NOK 20K / mo</option>
                  <option value="NOK 20K–50K/mo">NOK 20K – 50K / mo</option>
                  <option value="NOK 50K–150K/mo">NOK 50K – 150K / mo</option>
                  <option value="NOK 150K+/mo">NOK 150K+ / mo</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <Button
                data-testid="lead-submit"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg group"
              >
                {isSubmitting ? 'Sending…' : 'Book an App Growth Review'}
                {!isSubmitting && (
                  <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We reply within 1 working day. No spam — ever.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
